import type Phaser from 'phaser';

/**
 * Draw a dashed straight-line segment between two points using the currently
 * configured lineStyle. Dash and gap lengths are in pixels.
 */
export function drawDashedLine(
  gfx: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  dashLen: number,
  gapLen: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < 0.001) return;
  const ux = dx / len;
  const uy = dy / len;
  const period = dashLen + gapLen;
  let t = 0;
  while (t < len) {
    const end = Math.min(t + dashLen, len);
    gfx.lineBetween(x1 + ux * t, y1 + uy * t, x1 + ux * end, y1 + uy * end);
    t += period;
  }
}

/**
 * Draw a dashed rounded rectangle outline. Straight edges are dashed; the
 * four corner arcs are drawn solid because dashing along an arc with even
 * angular spacing reads as visually ragged against the straight edges.
 *
 * Caller should set lineStyle (width, color, alpha) before invoking.
 */
export function drawDashedRoundedRect(
  gfx: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  dashLen: number,
  gapLen: number,
): void {
  // Straight edges (corner-to-corner, excluding the rounded sections).
  drawDashedLine(gfx, x + r, y, x + w - r, y, dashLen, gapLen);
  drawDashedLine(gfx, x + w, y + r, x + w, y + h - r, dashLen, gapLen);
  drawDashedLine(gfx, x + w - r, y + h, x + r, y + h, dashLen, gapLen);
  drawDashedLine(gfx, x, y + h - r, x, y + r, dashLen, gapLen);

  // Solid quarter-arc corners (radius r) connecting the dashed edges.
  gfx.beginPath();
  gfx.arc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
  gfx.strokePath();
  gfx.beginPath();
  gfx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
  gfx.strokePath();
  gfx.beginPath();
  gfx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
  gfx.strokePath();
  gfx.beginPath();
  gfx.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
  gfx.strokePath();
}
